import { useMemo, useState, useEffect, ReactNode } from 'react';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Text, TextInput, ActionIcon, Stack, Button, Group, Modal, Paper } from '@mantine/core';
import { IconSearch, IconX, IconDownload, IconRefresh } from '@tabler/icons-react';
import { fetchLicences, updateLicence, deleteLicence, ApiError } from '../api/client';
import { LicenceRecord, UserRole } from '@lms/shared';
import { TableLoadingSkeleton } from './LoadingSkeleton';
import { LicenceDetailsModal } from './LicenceDetailsModal';
import { Charts } from './Charts';
import { exportLicencesToCsv } from '../utils/csv-export';
import { handleApiError, showSuccessNotification } from '../utils';
import { useAuth } from '../auth';
import 'mantine-datatable/styles.css';

interface LicenceTableProps {
  userRole?: UserRole;
}

export function LicenceTable({ userRole = 'Viewer' }: LicenceTableProps) {
  const [page, setPage] = useState(1);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<LicenceRecord>>({
    columnAccessor: 'customer_key',
    direction: 'asc',
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<LicenceRecord | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<LicenceRecord | null>(null);
  const [modalOpened, setModalOpened] = useState(false);

  const PAGE_SIZE = 50;
  const queryClient = useQueryClient();
  const { signOut } = useAuth();

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch licences data
  const { data, isLoading, error } = useQuery({
    queryKey: ['licences', page],
    queryFn: () => fetchLicences({ page, limit: PAGE_SIZE }),
    retry: (failureCount, error) => {
      // Don't retry on 401 or 403 errors
      if (error instanceof ApiError && (error.statusCode === 401 || error.statusCode === 403)) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
  });

  // Update selected record when data changes (to reflect backend updates like last_updated)
  useEffect(() => {
    if (selectedRecord && data?.licences) {
      const updatedRecord = data.licences.find(record => 
        record.customer_key === selectedRecord.customer_key
      );
      if (updatedRecord) {
        setSelectedRecord(updatedRecord);
      }
    }
  }, [data?.licences, selectedRecord?.customer_key]);

  // Handle query errors
  useEffect(() => {
    if (error) {
      handleApiError(error, () => {
        // On 401, sign out and redirect to login
        signOut();
      });
    }
  }, [error, signOut]);

  // Mutation for updating licence records with optimistic updates
  const updateMutation = useMutation({
    mutationFn: ({ customerKey, updates }: { customerKey: string; updates: Partial<LicenceRecord> }) =>
      updateLicence(customerKey, updates),
    onMutate: async ({ customerKey, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['licences'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<{ licences: LicenceRecord[] }>(['licences', page]);

      // Optimistically update to the new value
      if (previousData) {
        queryClient.setQueryData(['licences', page], {
          ...previousData,
          licences: previousData.licences.map((record) =>
            record.customer_key === customerKey
              ? { ...record, ...updates }
              : record
          ),
        });
      }

      // Update selected record if it matches
      if (selectedRecord?.customer_key === customerKey) {
        setSelectedRecord({ ...selectedRecord, ...updates });
      }

      // Return context with the previous data
      return { previousData };
    },
    onError: (err, _variables, context) => {
      // Revert to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(['licences', page], context.previousData);
      }

      // Handle error with centralized error handler
      handleApiError(err, () => {
        // On 401, sign out and redirect to login
        signOut();
      });
    },
    onSuccess: () => {
      // Show success notification
      showSuccessNotification('Success', 'Licence record updated successfully');

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['licences'] });
      
      // Update the selected record with fresh data from the server
      if (selectedRecord?.customer_key) {
        // The query invalidation will trigger a refetch, and we'll update selectedRecord 
        // when the new data comes in via useEffect
      }
    },
  });

  // Mutation for deleting licence records
  const deleteMutation = useMutation({
    mutationFn: (customerKey: string) => deleteLicence(customerKey),
    onSuccess: () => {
      // Show success notification
      showSuccessNotification('Success', 'Licence record deleted successfully');

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['licences'] });
      
      // Close modal
      setDeleteModalOpen(false);
      setRecordToDelete(null);
    },
    onError: (err) => {
      // Handle error with centralized error handler
      handleApiError(err, () => {
        // On 401, sign out and redirect to login
        signOut();
      });
    },
  });

  // Handle delete button click
  const handleDeleteClick = (record: LicenceRecord) => {
    setRecordToDelete(record);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!recordToDelete?.customer_key) {
      handleApiError(new Error('Cannot delete record without customer key'));
      return;
    }

    await deleteMutation.mutateAsync(recordToDelete.customer_key);
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  // Helper function to check if a value matches the search term (case-insensitive)
  const matchesSearch = (value: unknown, searchTerm: string): boolean => {
    if (!searchTerm) return true;
    if (value === null || value === undefined) return false;

    const lowerSearchTerm = searchTerm.toLowerCase();

    // Handle objects (like customer_details, entitlements)
    if (typeof value === 'object') {
      const jsonStr = JSON.stringify(value).toLowerCase();
      return jsonStr.includes(lowerSearchTerm);
    }

    // Handle primitives
    return String(value).toLowerCase().includes(lowerSearchTerm);
  };

  // Filter and sort records based on search term and sort status
  const filteredAndSortedRecords = useMemo(() => {
    if (!data?.licences) return [];

    // First, filter records based on search term
    let records = data.licences;
    if (searchTerm) {
      records = records.filter((record) => {
        // Check if any field matches the search term
        return Object.values(record).some((value) => matchesSearch(value, searchTerm));
      });
    }

    // Then, sort the filtered records
    const sortedRecords = [...records];
    const { columnAccessor, direction } = sortStatus;

    sortedRecords.sort((a, b) => {
      // Get values for comparison
      const aValue = a[columnAccessor as keyof LicenceRecord];
      const bValue = b[columnAccessor as keyof LicenceRecord];

      // Handle undefined/null values
      if (aValue === undefined || aValue === null) return direction === 'asc' ? 1 : -1;
      if (bValue === undefined || bValue === null) return direction === 'asc' ? -1 : 1;

      // Handle nested objects (like customer_details)
      if (typeof aValue === 'object' && typeof bValue === 'object') {
        const aStr = JSON.stringify(aValue);
        const bStr = JSON.stringify(bValue);
        return direction === 'asc' 
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      }

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle strings
      const aStr = String(aValue);
      const bStr = String(bValue);
      return direction === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return sortedRecords;
  }, [data?.licences, sortStatus, searchTerm]);

  // Helper function to highlight matching text
  const highlightText = (text: string | undefined | null): ReactNode => {
    if (!text || !searchTerm) return text ?? '-';
    
    const textStr = String(text);
    const lowerText = textStr.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    if (!lowerText.includes(lowerSearchTerm)) return textStr;

    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let index = lowerText.indexOf(lowerSearchTerm);
    let partIndex = 0;

    while (index !== -1) {
      // Add text before match
      if (index > lastIndex) {
        parts.push(<span key={partIndex++}>{textStr.substring(lastIndex, index)}</span>);
      }
      
      // Add highlighted match
      parts.push(
        <mark key={partIndex++} style={{ backgroundColor: '#ffeb3b', padding: '0 2px' }}>
          {textStr.substring(index, index + searchTerm.length)}
        </mark>
      );
      
      lastIndex = index + searchTerm.length;
      index = lowerText.indexOf(lowerSearchTerm, lastIndex);
    }

    // Add remaining text
    if (lastIndex < textStr.length) {
      parts.push(<span key={partIndex++}>{textStr.substring(lastIndex)}</span>);
    }

    return <>{parts}</>;
  };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      accessor: 'customer_key',
      title: 'Customer Key',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => (
        <Text 
          size="xs" 
          style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            maxWidth: '120px'
          }}
        >
          {highlightText(record.customer_key)}
        </Text>
      ),
    },
    {
      accessor: 'licence_type',
      title: 'Licence Type',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        const displayValue = record.licence_type === 'MARKETPLACE' ? 'Marketplace' : 
                           record.licence_type === 'PRIVATE' ? 'Private' : 
                           record.licence_type;
        const color = record.licence_type === 'MARKETPLACE' ? 'blue' : 'orange';
        return (
          <Text c={color} size="xs" fw={600}>
            {highlightText(displayValue)}
          </Text>
        );
      },
    },
    {
      accessor: 'status',
      title: 'Status',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        const displayValue = record.status === 'ACTIVE' ? 'Active' :
                           record.status === 'INACTIVE' ? 'Inactive' :
                           record.status === 'PENDING' ? 'Pending' :
                           record.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? 'Active (No Hub)' :
                           record.status;
        const color = record.status === 'ACTIVE' ? 'green' :
                     record.status === 'INACTIVE' ? 'red' :
                     record.status === 'PENDING' ? 'orange' :
                     record.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? 'yellow' :
                     'gray';
        return (
          <Text c={color} size="xs" fw={600}>
            {highlightText(displayValue)}
          </Text>
        );
      },
    },
    {
      accessor: 'valid_through',
      title: 'Valid Through',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        if (!record.valid_through) return highlightText('-');
        
        const date = new Date(record.valid_through);
        const londonTime = date.toLocaleString('en-GB', { 
          timeZone: 'Europe/London',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        const sydneyTime = date.toLocaleString('en-AU', { 
          timeZone: 'Australia/Sydney',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        return (
          <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
            <div>{highlightText(londonTime)} (LON)</div>
            <div style={{ color: '#666' }}>{highlightText(sydneyTime)} (SYD)</div>
          </div>
        );
      },
    },
    {
      accessor: 'customer_identifier',
      title: 'Customer Identifier',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        const name = record.customer_identifier;
        return highlightText(name || '-');
      },
    },
    {
      accessor: 'customer_details',
      title: 'Company',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        const company = record.customer_details?.company;
        return highlightText(company || '-');
      },
    },
    {
      accessor: 'customer_name',
      title: 'Customer Name',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        const name = record.customer_details?.name;
        return highlightText(name || '-');
      },
    },
    {
      accessor: 'registration_email',
      title: 'Registration Email',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        const regEmail = record.registration_email;
        const customerEmail = record.customer_details?.email;
        const emailsMatch = regEmail === customerEmail;
        
        return (
          <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
            <Text 
              size="xs" 
              c={emailsMatch ? undefined : 'red'}
              fw={emailsMatch ? undefined : 600}
            >
              {highlightText(regEmail)}
            </Text>
            {!emailsMatch && customerEmail && (
              <Text size="xs" c="orange" style={{ fontStyle: 'italic' }}>
                Customer details e-mail: {highlightText(customerEmail)}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      accessor: 'max_accounts',
      title: 'Accounts',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        const current = record.current_account_count || 0;
        const max = record.max_accounts || 0;
        return highlightText(`${current} / ${max}`);
      },
    },
    {
      accessor: 'version',
      title: 'Version',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        if (record.version === undefined || record.version === null) return '';
        return highlightText(String(record.version));
      },
    },
    {
      accessor: 'entitlements',
      title: 'Marketplace Entitlements',
      sortable: true,
      resizable: true,
      render: (record: LicenceRecord) => {
        if (!record.entitlements || !Array.isArray(record.entitlements) || record.entitlements.length === 0) return '';
        
        return (
          <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
            {record.entitlements.map((entitlement, index) => (
              <div key={index}>
                {entitlement.account_count && (
                  <div>{highlightText(`Accounts: ${entitlement.account_count}`)}</div>
                )}
                {entitlement.expiration_date && (
                  <div style={{ color: '#666' }}>
                    {highlightText(`Expires: ${new Date(entitlement.expiration_date).toLocaleDateString('en-GB', { 
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}`)}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
  ], [searchTerm, highlightText]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  // Handle CSV export
  const handleExport = () => {
    try {
      // Export only the filtered records (or all records if no search is active)
      exportLicencesToCsv(filteredAndSortedRecords);
      
      showSuccessNotification(
        'Export Successful',
        `Exported ${filteredAndSortedRecords.length} record(s) to CSV`
      );
    } catch (err) {
      handleApiError(err);
    }
  };

  // Loading skeleton component
  if (isLoading && !data) {
    return <TableLoadingSkeleton />;
  }

  // Error state
  if (error && !data) {
    return (
      <Paper shadow="sm" p="xl" radius="md" withBorder>
        <Stack align="center" gap="md">
          <Text c="red" size="lg" fw={500}>
            Error loading licences
          </Text>
          <Text c="dimmed" size="sm">
            {error instanceof Error ? error.message : 'Unknown error'}
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      {/* Charts */}
      {data?.licences && data.licences.length > 0 && (
        <Charts licences={filteredAndSortedRecords} />
      )}

      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="md">
            <TextInput
              placeholder="Search across all fields..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              rightSection={
                searchInput ? (
                  <ActionIcon
                    variant="subtle"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                ) : null
              }
              style={{ 
                flex: 1,
                minWidth: '250px',
                maxWidth: '400px',
              }}
            />
            
            <Group gap="sm">
              <Button
                leftSection={<IconRefresh size={16} style={{ animation: isLoading ? 'spin 1s linear infinite' : undefined }} />}
                onClick={() => queryClient.invalidateQueries({ queryKey: ['licences'] })}
                variant="filled"
                color="blue"
                loading={isLoading}
              >
                Refresh
              </Button>
              
              <Button
                leftSection={<IconDownload size={16} />}
                onClick={handleExport}
                variant="light"
                disabled={filteredAndSortedRecords.length === 0}
              >
                Export to CSV
              </Button>
            </Group>
          </Group>
          
          {filteredAndSortedRecords.length === 0 && !isLoading && searchTerm ? (
            <Box 
              p="xl" 
              style={{ 
                textAlign: 'center',
                backgroundColor: 'var(--mantine-color-gray-0)',
                borderRadius: 'var(--mantine-radius-md)',
              }}
            >
              <Text c="dimmed" size="lg" fw={500}>
                No results found for "{searchTerm}"
              </Text>
              <Text c="dimmed" size="sm" mt="xs">
                Try adjusting your search term
              </Text>
            </Box>
          ) : (
            <Box
              style={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <DataTable
                columns={columns}
                records={filteredAndSortedRecords}
                idAccessor="customer_key"
                fetching={isLoading}
                totalRecords={filteredAndSortedRecords.length}
                recordsPerPage={PAGE_SIZE}
                page={page}
                onPageChange={setPage}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                minHeight={400}
                striped
                highlightOnHover
                withTableBorder
                withColumnBorders
                storeColumnsKey="licence-table-columns"
                onRowClick={({ record }) => {
                  setSelectedRecord(record);
                  setModalOpened(true);
                }}
                noRecordsText={searchTerm ? `No results found for "${searchTerm}"` : 'No records to display'}
                rowStyle={(record) => {
                  if (record.valid_through) {
                    const validThrough = new Date(record.valid_through);
                    const now = new Date();
                    if (validThrough < now) {
                      return { 
                        outline: '1.5px dashed #CC0000',
                        outlineOffset: '-4px'
                      };
                    }
                  }
                  return undefined;
                }}
                styles={{
                  table: {
                    borderRadius: 'var(--mantine-radius-md)',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                  },
                  header: {
                    backgroundColor: 'var(--mantine-color-gray-1)',
                    fontWeight: 600,
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    padding: '8px',
                  },
                  root: {
                    fontSize: '12px',
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      </Paper>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Licence Details Modal */}
      <LicenceDetailsModal
        licence={selectedRecord}
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setSelectedRecord(null);
        }}
        userRole={userRole}
        onUpdate={(updates) => {
          if (selectedRecord?.customer_key) {
            updateMutation.mutate({
              customerKey: selectedRecord.customer_key,
              updates,
            });
          }
        }}
        onDelete={userRole === 'Admin' && selectedRecord ? () => handleDeleteClick(selectedRecord) : undefined}
      />

      {/* Delete confirmation modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={handleDeleteCancel}
        title={
          <Text fw={600} size="lg">
            Confirm Deletion
          </Text>
        }
        centered
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the licence record for customer{' '}
            <strong>{recordToDelete?.customer_key}</strong>?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm" mt="md">
            <Button variant="default" onClick={handleDeleteCancel}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteConfirm}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
